// ─── PIPELINE SLOT DEFINITIONS ──────────────────────────────────────────────
// Defines the visual stepper slots for both Suggerisci and Crea Contenuto.
// Each slot: { id, label, skippable? }
// Phase mappers: backend status string → { slotId, subLabel }

// ── Suggerisci (brief generation) — 3 fixed slots ──────────────────────────

export const SUGGEST_SLOTS = [
  { id: 'setup',      label: 'Avvio' },
  { id: 'generating', label: 'Generazione' },
  { id: 'done',       label: 'Completato' },
];

/**
 * Map SSE event type → activePhase for PipelineStepper.
 * @param {string} eventType - 'starting' | 'generating' | 'complete' | 'error'
 * @returns {{ slotId: string, subLabel: string } | null}
 */
export function mapSuggestEvent(eventType) {
  switch (eventType) {
    case 'starting':
      return { slotId: 'setup', subLabel: 'Preparazione richiesta...' };
    case 'generating':
      return { slotId: 'generating', subLabel: 'Generazione suggerimenti...' };
    case 'complete':
      return null; // signals completion
    default:
      return null;
  }
}

// ── Crea Contenuto (pipeline) — 4 slots (1 skippable) ──────────────────────
// Fact-checking is integrated into the Critic (REVIEWING stage), not a separate step.

/**
 * Build the slot array for content pipeline, marking skipped slots.
 * @param {object} opts
 * @param {boolean} opts.researchRequested - whether research phase runs
 * @returns {Array<{ id: string, label: string, skipped: boolean }>}
 */
export function buildPipelineSlots({ researchRequested }) {
  return [
    { id: 'setup',     label: 'Avvio',          skipped: false },
    { id: 'research',  label: 'Ricerca',        skipped: !researchRequested },
    { id: 'writing',   label: 'Scrittura',      skipped: false },
    { id: 'finishing',  label: 'Finalizzazione', skipped: false },
  ];
}

/**
 * Map backend pipeline status → activePhase for PipelineStepper.
 * @param {object} statusResponse - from GET /pipeline/run/{id}/status
 *   Expected shape: { status: string, iteration_count?: number }
 * @returns {{ slotId: string, subLabel: string } | null}
 */
export function mapPipelineStatus(statusResponse) {
  const { status, iteration_count } = statusResponse;
  switch (status) {
    case 'CREATED':
      return { slotId: 'setup', subLabel: 'Avvio pipeline...' };
    case 'RESEARCHING':
      return { slotId: 'research', subLabel: 'Ricerca fonti...' };
    case 'DRAFTING':
      return { slotId: 'writing', subLabel: 'Scrittura bozza...' };
    case 'REVIEWING':
      return { slotId: 'writing', subLabel: 'Revisione e verifica...' };
    case 'REVISING': {
      // BE iteration_count counts the *next* iteration about to start
      // (drafting=1, first revision=2, etc.) — display as 1-based rewrite #.
      const n = iteration_count > 1 ? iteration_count - 1 : 1;
      return { slotId: 'writing', subLabel: `Riscrittura (${n}/3)...` };
    }
    case 'STYLING':
      return { slotId: 'finishing', subLabel: 'Styling finale...' };
    case 'COMPLETED':
    case 'approved':
    case 'completed':
      return null; // signals completion
    case 'FAILED':
    case 'failed':
      return null; // caller checks isError separately
    default:
      // Defensive: unknown status — keep last known phase
      return null;
  }
}
